import { tableFromIPC } from "apache-arrow";
import Papa from "papaparse";
import { readParquet } from "parquet-wasm/esm";
import * as XLSX from "xlsx";
import type { Clipboard } from "@/types/workspace/Clipboard";

/**
 * Recursively flattens a nested object structure into a single-level object
 * with keys separated by underscores
 */
function flattenObject(obj: any, prefix = ""): any {
  const flattened: any = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}_${key}` : key;

      if (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        Object.assign(flattened, flattenObject(value, newKey));
      } else {
        flattened[newKey] = value;
      }
    }
  }

  return flattened;
}

class FileParserService {
  /**
   * Parses a CSV file and returns structured data with rows, columns, and metadata
   */
  async parseCSV(file: File): Promise<Clipboard> {
    return new Promise(async (resolve, reject) => {
      try {
        // Explicitly read file as UTF-8 using ArrayBuffer and TextDecoder
        // This ensures proper handling of special characters like æ, ø, å
        const arrayBuffer = await file.arrayBuffer();
        const decoder = new TextDecoder("utf-8");
        let text = decoder.decode(arrayBuffer);

        // Remove UTF-8 BOM if present (U+FEFF)
        if (text.charCodeAt(0) === 0xfeff) {
          text = text.slice(1);
        }

        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          complete: (results: Papa.ParseResult<any>) => {
            const data = results.data as any[];
            const columns = results.meta.fields || [];

            resolve({
              rows: data,
              columns,
              metadata: {
                rowCount: data.length,
                columnCount: columns.length,
                type: "csv",
              },
            });
          },
          error: (error: Error) => {
            reject(new Error(`CSV parsing error: ${error.message}`));
          },
        });
      } catch (error) {
        reject(
          new Error(
            `CSV parsing error: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          )
        );
      }
    });
  }

  /**
   * Parses a JSON file and returns structured data
   * Supports arrays, single objects, and GeoJSON FeatureCollections
   */
  async parseJSON(file: File): Promise<Clipboard> {
    return new Promise(async (resolve, reject) => {
      try {
        const text = await file.text();
        const data = JSON.parse(text);

        let rows: any[];
        let columns: string[];

        if (Array.isArray(data)) {
          rows = data.map((obj) => flattenObject(obj));
          columns = rows.length > 0 ? Object.keys(rows[0]) : [];
        } else if (data && typeof data === "object") {
          if (data.type === "FeatureCollection") {
            const features = data.features || [];
            rows = features.map((feature: any) => {
              const flattenedProps = flattenObject(feature.properties || {});
              return {
                ...flattenedProps,
                geometry: feature.geometry,
              };
            });
            columns = rows.length > 0 ? Object.keys(rows[0]) : [];
          } else {
            rows = [flattenObject(data)];
            columns = Object.keys(rows[0]);
          }
        } else {
          reject(new Error("Invalid JSON structure"));
          return;
        }

        resolve({
          rows,
          columns,
          metadata: {
            rowCount: rows.length,
            columnCount: columns.length,
            type: "json",
          },
        });
      } catch (error) {
        reject(
          new Error(
            `JSON parsing error: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          )
        );
      }
    });
  }

  /**
   * Parses a GeoJSON file and returns structured data with flattened properties
   */
  async parseGeoJSON(file: File): Promise<Clipboard> {
    return new Promise(async (resolve, reject) => {
      try {
        const text = await file.text();
        const geoJSON = JSON.parse(text);

        if (!geoJSON || geoJSON.type !== "FeatureCollection") {
          reject(new Error("Invalid GeoJSON: expected FeatureCollection"));
          return;
        }

        const features = geoJSON.features || [];
        const rows = features.map((feature: any) => {
          const flattenedProps = flattenObject(feature.properties || {});
          return {
            ...flattenedProps,
            geometry: feature.geometry,
          };
        });

        const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

        resolve({
          rows,
          columns,
          metadata: {
            rowCount: rows.length,
            columnCount: columns.length,
            type: "geojson",
          },
        });
      } catch (error) {
        reject(
          new Error(
            `GeoJSON parsing error: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          )
        );
      }
    });
  }

  async parseExcel(file: File): Promise<Clipboard> {
    return new Promise(async (resolve, reject) => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        // XLSX.read should handle UTF-8 encoding correctly by default
        // The library properly reads Unicode strings from Excel files
        const workbook = XLSX.read(arrayBuffer, {
          type: "array",
          cellDates: true,
        });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        // sheet_to_json preserves UTF-8 encoding in string values by default
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: null,
        });

        const rows = jsonData.map((obj: any) => flattenObject(obj));
        const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

        resolve({
          rows,
          columns,
          metadata: {
            rowCount: rows.length,
            columnCount: columns.length,
            type: "excel",
          },
        });
      } catch (error) {
        reject(
          new Error(
            `Excel parsing error: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          )
        );
      }
    });
  }

  async parseParquet(file: File): Promise<Clipboard> {
    return new Promise(async (resolve, reject) => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const parquetUint8Array = new Uint8Array(arrayBuffer);
        const arrowWasmTable = readParquet(parquetUint8Array);
        const ipcStream = arrowWasmTable.intoIPCStream();
        const arrowTable = tableFromIPC(ipcStream);
        const numRows = arrowTable.numRows;
        const schema = arrowTable.schema;

        const rows: any[] = [];
        for (let i = 0; i < numRows; i++) {
          const row: any = {};
          for (const field of schema.fields) {
            const column = arrowTable.getChild(field.name);
            if (column) {
              const value = column.get(i);
              row[field.name] = value;
            }
          }
          rows.push(flattenObject(row));
        }

        const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

        resolve({
          rows,
          columns,
          metadata: {
            rowCount: rows.length,
            columnCount: columns.length,
            type: "parquet",
          },
        });
      } catch (error) {
        reject(
          new Error(
            `Parquet parsing error: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          )
        );
      }
    });
  }

  /**
   * Detects the file type based on file extension or MIME type
   */
  detectFileType(file: File): "csv" | "json" | "geojson" | "excel" | "parquet" {
    const extension = file.name.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "csv":
        return "csv";
      case "json":
        return "json";
      case "geojson":
        return "geojson";
      case "xlsx":
      case "xls":
        return "excel";
      case "parquet":
        return "parquet";
      default:
        if (file.type === "text/csv") return "csv";
        if (
          file.type === "application/json" ||
          file.type === "application/geo+json"
        )
          return "json";
        if (
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "application/vnd.ms-excel"
        )
          return "excel";
        return "json";
    }
  }

  /**
   * Automatically parses a file by detecting its type
   */
  async parseFile(file: File): Promise<Clipboard> {
    const fileType = this.detectFileType(file);

    switch (fileType) {
      case "csv":
        return this.parseCSV(file);
      case "geojson":
        return this.parseGeoJSON(file);
      case "json":
        return this.parseJSON(file);
      case "excel":
        return this.parseExcel(file);
      case "parquet":
        return this.parseParquet(file);
      default:
        throw new Error(`Unsupported file type: ${fileType}`);
    }
  }

  /**
   * Converts parsed rows to GeoJSON FeatureCollection format
   */
  rowsToGeoJSON(rows: any[]): GeoJSON.FeatureCollection {
    const features = rows.map((row) => {
      const { geometry, ...properties } = row;
      return {
        type: "Feature" as const,
        properties,
        geometry: geometry || null,
      };
    });

    return {
      type: "FeatureCollection",
      features,
    };
  }

  /**
   * Checks if rows contain geometry data
   */
  hasGeometryData(rows: any[]): boolean {
    return rows.some((row) => row.geometry && typeof row.geometry === "object");
  }

  /**
   * Detects if columns contain latitude and longitude data
   * Supports common variations: latitude/lat, longitude/lon/lng, x/y coordinates
   */
  hasLatLonColumns(columns: string[]): {
    has: boolean;
    lat?: string;
    lon?: string;
  } {
    // Priority order for latitude column detection (most specific first)
    const latPatterns = [
      /^latitude$/i, // Exact match
      /^lat$/i, // Exact "lat"
      /^y$/i, // Y coordinate
      /latitude/i, // Contains "latitude"
      /^lat\b/i, // Starts with "lat" word boundary
    ];

    // Priority order for longitude column detection
    const lonPatterns = [
      /^longitude$/i, // Exact match
      /^lon$/i, // Exact "lon"
      /^lng$/i, // Exact "lng"
      /^x$/i, // X coordinate
      /longitude/i, // Contains "longitude"
      /^lon\b/i, // Starts with "lon" word boundary
      /^lng\b/i, // Starts with "lng" word boundary
    ];

    let latCol: string | undefined;
    let lonCol: string | undefined;

    // Find latitude column using priority patterns
    for (const pattern of latPatterns) {
      const match = columns.find((col) => pattern.test(col));
      if (match) {
        latCol = match;
        break;
      }
    }

    // Find longitude column using priority patterns
    for (const pattern of lonPatterns) {
      const match = columns.find(
        (col) => pattern.test(col) && col !== latCol // Don't use same column for both
      );
      if (match) {
        lonCol = match;
        break;
      }
    }

    return {
      has: !!(latCol && lonCol),
      lat: latCol,
      lon: lonCol,
    };
  }
}

export const fileParserService = new FileParserService();
